// components/Modals/YouTubeShortsModal/YouTubeShortsModal.js
import React, { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { youtubeScrapeAPI } from '../../../services/api';
import { API_URL } from '../../../config';
import PlatformSettingsPanel from '../../Shared/PlatformSettingsPanel/PlatformSettingsPanel';
import './YouTubeShortsModal.css';

const YouTubeShortsModal = ({ isOpen, onClose, tags, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState('');
  const [videoData, setVideoData] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Bulk mode
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkUrls, setBulkUrls] = useState('');

  // Step 3: İndirme ilerleme
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const cancelRef = useRef(false);

  // Platform Ayarları
  const [contentText, setContentText] = useState('');
  const [buttonForm, setButtonForm] = useState([]);
  const [youtubeSettings, setYoutubeSettings] = useState({
    title: '', description: '', categoryId: '24'
  });
  const [xSettings, setXSettings] = useState({ text: '' });
  const [tiktokSettings, setTiktokSettings] = useState({
    caption: '', hashtags: '', allowComments: true, allowDuet: true
  });
  const [instagramSettings, setInstagramSettings] = useState({
    contentType: 'post', caption: '', firstComment: '', collaborators: '', userTags: ''
  });
  const [showPlatformSettings, setShowPlatformSettings] = useState(false);

  const resetModal = () => {
    setStep(1);
    setUrl('');
    setVideoData(null);
    setSelectedTags([]);
    setIsLoading(false);
    setBulkMode(false);
    setBulkUrls('');
    setIsImporting(false);
    setImportProgress(0);
    setSavedCount(0);
    setErrorCount(0);
    setTotalCount(0);
    setCurrentIndex(0);
    setIsCompleted(false);
    setBulkResults([]);
    cancelRef.current = false;
    setContentText('');
    setButtonForm([]);
    setYoutubeSettings({ title: '', description: '', categoryId: '24' });
    setXSettings({ text: '' });
    setTiktokSettings({ caption: '', hashtags: '', allowComments: true, allowDuet: true });
    setInstagramSettings({ contentType: 'post', caption: '', firstComment: '', collaborators: '', userTags: '' });
    setShowPlatformSettings(false);
  };

  const handleClose = () => {
    if (isImporting && !isCompleted) {
      if (!window.confirm('İşlem devam ediyor. İptal etmek istiyor musunuz?')) {
        return;
      }
      cancelRef.current = true;
    }
    resetModal();
    onClose();
  };

  // Step 1: Önizleme
  const handlePreview = async () => {
    const input = url.trim();
    if (!input) {
      toast.error('YouTube URL girin');
      return;
    }

    setIsLoading(true);
    try {
      const response = await youtubeScrapeAPI.preview(input);
      if (response.data.success) {
        setVideoData(response.data.data);
        setStep(2);
      } else {
        toast.error(response.data.error || 'Video bilgisi alınamadı');
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Video bilgisi alınamadı';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Platform config oluştur
  const buildPlatformConfig = () => {
    const pc = {};

    if (contentText) pc.content = contentText;
    if (buttonForm && buttonForm.length > 0) pc.buttons = buttonForm;

    if (youtubeSettings.title) pc.youtubeTitle = youtubeSettings.title;
    if (youtubeSettings.description) pc.youtubeDescription = youtubeSettings.description;
    if (youtubeSettings.categoryId) pc.youtubeCategoryId = youtubeSettings.categoryId;
    pc.isYoutubeShort = true;

    if (xSettings.text) pc.xText = xSettings.text;

    if (tiktokSettings.caption) pc.tiktokCaption = tiktokSettings.caption;
    if (tiktokSettings.hashtags) pc.tiktokHashtags = tiktokSettings.hashtags;

    if (instagramSettings.caption) pc.instagramCaption = instagramSettings.caption;

    return Object.keys(pc).length > 0 ? pc : null;
  };

  // Step 2 → Step 3: Import başlat
  const handleStartImport = async () => {
    if (selectedTags.length === 0) {
      toast.error('En az bir etiket seçmelisiniz');
      return;
    }

    setStep(3);
    setIsImporting(true);
    cancelRef.current = false;

    const platformConfig = buildPlatformConfig();

    if (bulkMode) {
      await handleBulkImport(platformConfig);
    } else {
      await handleSingleImport(platformConfig);
    }
  };

  // Tek video import
  const handleSingleImport = async (platformConfig) => {
    setTotalCount(1);
    setCurrentIndex(1);

    try {
      const response = await youtubeScrapeAPI.importSingle(
        url.trim(), selectedTags, platformConfig
      );

      if (response.data.success) {
        setSavedCount(1);
        setImportProgress(100);
        toast.success('Video kütüphaneye eklendi!');
      } else {
        setErrorCount(1);
        toast.error(response.data.error || 'Video eklenemedi');
      }
    } catch (error) {
      setErrorCount(1);
      toast.error(error.response?.data?.error || 'Bağlantı hatası');
    }

    setIsCompleted(true);
  };

  // Toplu import
  const handleBulkImport = async (platformConfig) => {
    const urls = bulkUrls
      .split('\n')
      .map(u => u.trim())
      .filter(u => u.length > 0);

    setTotalCount(urls.length);
    let saved = 0;
    let errors = 0;
    const results = [];

    for (let i = 0; i < urls.length; i++) {
      if (cancelRef.current) break;

      setCurrentIndex(i + 1);
      setImportProgress(Math.round(((i + 1) / urls.length) * 100));

      try {
        const response = await youtubeScrapeAPI.importSingle(
          urls[i], selectedTags, platformConfig
        );

        if (response.data.success) {
          saved++;
          setSavedCount(saved);
          results.push({ url: urls[i], success: true, title: response.data.data.title });
        } else {
          errors++;
          setErrorCount(errors);
          results.push({ url: urls[i], success: false, error: response.data.error });
        }
      } catch (error) {
        errors++;
        setErrorCount(errors);
        results.push({ url: urls[i], success: false, error: 'Bağlantı hatası' });
      }
    }

    setBulkResults(results);
    setIsCompleted(true);

    if (saved > 0) {
      toast.success(`${saved} video kütüphaneye eklendi!`, { duration: 5000 });
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return '';
    return `${dateStr.substring(6, 8)}.${dateStr.substring(4, 6)}.${dateStr.substring(0, 4)}`;
  };

  const getBulkUrlCount = () => {
    return bulkUrls.split('\n').filter(u => u.trim().length > 0).length;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="yt-shorts-modal">

        {/* Header */}
        <div className="ytm-header">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0C.488 3.45.029 5.804 0 12c.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0C23.512 20.55 23.971 18.196 24 12c-.029-6.185-.484-8.549-4.385-8.816zM9 16V8l8 4-8 4z"/>
            </svg>
            YouTube Shorts Çek
          </h3>
          <div className="ytm-steps">
            <span className={`ytm-step ${step >= 1 ? 'active' : ''}`}>1. URL</span>
            <span className="ytm-step-arrow">→</span>
            <span className={`ytm-step ${step >= 2 ? 'active' : ''}`}>2. Ayarlar</span>
            <span className="ytm-step-arrow">→</span>
            <span className={`ytm-step ${step >= 3 ? 'active' : ''}`}>3. İndir</span>
          </div>
          <button className="ytm-close" onClick={handleClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="ytm-body">

          {/* Step 1: URL Giriş */}
          {step === 1 && (
            <div className="ytm-step-content">
              <div className="ytm-url-section">
                <label>YouTube Video URL</label>

                {!bulkMode ? (
                  <>
                    <div className="ytm-url-input-wrapper">
                      <input
                        type="text"
                        value={url}
                        onChange={e => setUrl(e.target.value)}
                        placeholder="https://www.youtube.com/shorts/..."
                        className="ytm-url-input"
                        onKeyDown={e => e.key === 'Enter' && !isLoading && handlePreview()}
                        autoFocus
                      />
                      <button
                        onClick={handlePreview}
                        disabled={isLoading || !url.trim()}
                        className="ytm-btn-primary"
                      >
                        {isLoading ? 'Çekiliyor...' : 'Getir'}
                      </button>
                    </div>
                    <p className="ytm-hint">YouTube Shorts, normal video veya youtu.be linki desteklenir</p>
                  </>
                ) : (
                  <>
                    <textarea
                      value={bulkUrls}
                      onChange={e => setBulkUrls(e.target.value)}
                      placeholder="Her satıra bir URL yazın:&#10;https://www.youtube.com/shorts/abc123&#10;https://www.youtube.com/shorts/def456&#10;https://youtu.be/ghi789"
                      className="ytm-bulk-textarea"
                      autoFocus
                    />
                    <p className="ytm-hint">
                      {getBulkUrlCount()} URL girildi
                    </p>

                    <div className="ytm-actions" style={{ marginTop: '12px' }}>
                      <button onClick={() => setBulkMode(false)} className="ytm-btn-secondary">
                        ← Tekli Moda Dön
                      </button>
                      <button
                        onClick={() => {
                          if (getBulkUrlCount() === 0) {
                            toast.error('En az bir URL girin');
                            return;
                          }
                          setStep(2);
                        }}
                        disabled={getBulkUrlCount() === 0}
                        className="ytm-btn-primary"
                      >
                        Devam ({getBulkUrlCount()} video)
                      </button>
                    </div>
                  </>
                )}

                {!bulkMode && (
                  <div className="ytm-bulk-section">
                    <button
                      className="ytm-bulk-toggle"
                      onClick={() => setBulkMode(true)}
                    >
                      📋 Toplu URL eklemek için tıklayın
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Önizleme + Ayarlar */}
          {step === 2 && (
            <div className="ytm-step-content">

              {/* Tek video önizleme */}
              {!bulkMode && videoData && (
                <div className="ytm-video-card">
                  <div className="ytm-video-thumbnail">
                    <img
                      src={youtubeScrapeAPI.proxyThumbnail(videoData.thumbnail)}
                      alt={videoData.title}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="ytm-video-duration">
                      {formatDuration(videoData.duration)}
                    </span>
                  </div>
                  <div className="ytm-video-info">
                    <h4 className="ytm-video-title">{videoData.title}</h4>
                    <div className="ytm-video-meta">
                      <span>👁️ {formatNumber(videoData.viewCount)}</span>
                      <span>❤️ {formatNumber(videoData.likeCount)}</span>
                      <span>💬 {formatNumber(videoData.commentCount)}</span>
                      {videoData.uploadDate && (
                        <span>📅 {formatDate(videoData.uploadDate)}</span>
                      )}
                    </div>
                    <div className="ytm-video-channel">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      <span>{videoData.uploader}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Bulk mode bilgi */}
              {bulkMode && (
                <div className="ytm-video-card">
                  <div className="ytm-video-info">
                    <h4 className="ytm-video-title">📋 Toplu İçe Aktarma</h4>
                    <div className="ytm-video-meta">
                      <span>📺 {getBulkUrlCount()} video</span>
                    </div>
                    <p className="ytm-hint" style={{ marginTop: '8px' }}>
                      Tüm videolar aynı etiket ve platform ayarlarıyla eklenecek.
                    </p>
                  </div>
                </div>
              )}

              {/* Etiket Seçimi */}
              <div className="ytm-tags-section">
                <label>Etiket Seçin <span className="ytm-required">*</span></label>
                {tags && tags.length > 0 ? (
                  <div className="ytm-tags-list">
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        className={`ytm-tag ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                        onClick={() => toggleTag(tag.id)}
                        style={{
                          backgroundColor: selectedTags.includes(tag.id) ? tag.color : 'transparent',
                          borderColor: tag.color,
                          color: selectedTags.includes(tag.id) ? '#fff' : tag.color
                        }}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="ytm-no-tags">Henüz etiket oluşturulmamış.</p>
                )}
              </div>

              {/* Platform Ayarları Toggle */}
              <div className="ytm-platform-toggle">
                <button
                  type="button"
                  className={`ytm-platform-toggle-btn ${showPlatformSettings ? 'active' : ''}`}
                  onClick={() => setShowPlatformSettings(!showPlatformSettings)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                  <span>Platform Ayarları {showPlatformSettings ? '(Gizle)' : '(Göster)'}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: showPlatformSettings ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s', marginLeft: 'auto' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <span className="ytm-platform-hint">
                  Opsiyonel - Telegram, YouTube, X, TikTok, Instagram ayarları
                </span>
              </div>

              {showPlatformSettings && (
                <div className="ytm-platform-settings-wrapper">
                  <PlatformSettingsPanel
                    contentText={contentText}
                    onContentTextChange={setContentText}
                    buttonForm={buttonForm}
                    onButtonFormChange={setButtonForm}
                    youtubeSettings={youtubeSettings}
                    onYoutubeSettingsChange={setYoutubeSettings}
                    xSettings={xSettings}
                    onXSettingsChange={setXSettings}
                    tiktokSettings={tiktokSettings}
                    onTiktokSettingsChange={setTiktokSettings}
                    instagramSettings={instagramSettings}
                    onInstagramSettingsChange={setInstagramSettings}
                    hasVideo={true}
                    hasPhoto={false}
                    bulkMode={true}
                    selectedTagIds={selectedTags}
                    tags={tags}
                    compact={true}
                  />
                </div>
              )}

              <div className="ytm-actions">
                <button onClick={() => { setStep(1); setVideoData(null); }} className="ytm-btn-secondary">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  Geri
                </button>
                <button
                  onClick={handleStartImport}
                  disabled={selectedTags.length === 0}
                  className="ytm-btn-success"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  {bulkMode ? `${getBulkUrlCount()} Video İndir` : 'İndir ve Ekle'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: İndirme İlerlemesi */}
          {step === 3 && (
            <div className="ytm-step-content">

              {!isCompleted ? (
                <div className="ytm-processing">
                  <div className="ytm-spinner"></div>
                  <h4>{bulkMode ? 'Videolar İndiriliyor...' : 'Video İndiriliyor...'}</h4>

                  <div className="ytm-progress-section">
                    <div className="ytm-progress-bar-wrapper">
                      <div className="ytm-progress-bar" style={{ width: `${importProgress}%` }} />
                    </div>
                    <div className="ytm-progress-text">
                      <span>%{importProgress}</span>
                      <span>{currentIndex} / {totalCount}</span>
                    </div>
                  </div>

                  {bulkMode && (
                    <div className="ytm-download-stats">
                      <div className="ytm-dstat">
                        <span className="ytm-dstat-label">Kaydedilen</span>
                        <span className="ytm-dstat-value ytm-success-text">{savedCount}</span>
                      </div>
                      <div className="ytm-dstat">
                        <span className="ytm-dstat-label">Hata</span>
                        <span className="ytm-dstat-value ytm-error-text">{errorCount}</span>
                      </div>
                    </div>
                  )}

                  <div className="ytm-current-item">
                    <div className="ytm-current-dot"></div>
                    <span>Video indiriliyor ve kütüphaneye ekleniyor...</span>
                  </div>

                  {bulkMode && (
                    <div className="ytm-actions" style={{ marginTop: '20px', justifyContent: 'center' }}>
                      <button onClick={() => { cancelRef.current = true; }} className="ytm-btn-danger">
                        İptal Et
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="ytm-completed-section">
                    <div className="ytm-completed-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4ecb71" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    </div>
                    <h4>İşlem Tamamlandı!</h4>
                    <p>{savedCount} video başarıyla kütüphaneye eklendi.</p>
                    {errorCount > 0 && (
                      <p className="ytm-error-note">{errorCount} video eklenemedi.</p>
                    )}
                  </div>

                  {/* Bulk sonuçları */}
                  {bulkResults.length > 0 && (
                    <div className="ytm-bulk-results">
                      {bulkResults.map((r, i) => (
                        <div key={i} className={`ytm-bulk-result-item ${r.success ? 'success' : 'error'}`}>
                          <span>{r.success ? '✅' : '❌'}</span>
                          <span className="ytm-bulk-result-url">{r.title || r.url}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="ytm-actions" style={{ justifyContent: 'center' }}>
                    <button onClick={() => { resetModal(); onClose(); if (onSuccess) onSuccess(); }} className="ytm-btn-primary">
                      Tamam, Kapat
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YouTubeShortsModal;